import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../context/AuthContext";
import { api, CATEGORY_GROUPS, STATUS_COLORS, DECISION_SECTIONS } from "../lib/api";
import { DarkPortalShell, AttachmentList, NewBadge } from "../components/Shell";
import { useNewFlags } from "../hooks/useNewFlags";
import ReviewWorkspace, { WORKING_STATUSES } from "../components/ReviewWorkspace";
import DocViewer from "../components/DocViewer";
import { ReviewWindowCards } from "../components/ReviewWindowCards";
import { ClientAccount } from "../components/ClientAccount";
import { DocSearchBar, filterDocs } from "../components/DocSearchBar";
import { ReplyBubble, ReplyToggle } from "../components/ConversationThread";
import { ReviewFeedbackTab } from "./ReviewFeedback";
import { BlockerBadge } from "../components/BlockerControl";
import { toast } from "sonner";
import { ChevronDown, CheckCircle2, FileText, Send, Loader2, Eye, ClipboardList, ShieldCheck, BookOpen, DollarSign, ArrowRightCircle, Upload, Bell, MessageSquare, MessageCircle, AlertTriangle } from "lucide-react";

const REVIEW_NAV = [
  { id: "documents", label: "Documents & Decisions", icon: BookOpen },
  { id: "feedback", label: "Feedback & Replies", icon: MessageSquare },
  { id: "billing", label: "Billing", icon: DollarSign },
];

function ReviewNav({ items, view, onChange, badges, warnBadges }) {
  return (
    <nav className="flex gap-1 mb-8 overflow-x-auto p-1 rounded-xl" style={{ backgroundColor: "#112A57" }} data-testid="review-nav">
      {items.map((n) => {
        const Icon = n.icon;
        const active = view === n.id;
        const badge = badges?.[n.id] || 0;
        const warn = warnBadges?.[n.id] || 0;
        return (
          <button key={n.id} data-testid={`review-nav-${n.id}`} onClick={() => onChange(n.id)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors"
            style={active ? { backgroundColor: "#F97316", color: "#fff" } : { color: "#9CA3AF" }}>
            <Icon size={16} /> {n.label}
            {warn > 0 && (
              <span data-testid={`review-nav-blocker-${n.id}`} title={`${warn} item(s) holding up the build`}
                className="inline-flex items-center gap-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold justify-center"
                style={{ backgroundColor: "#ffe606", color: "#0A1F44" }}>
                <AlertTriangle size={10} />{warn}
              </span>
            )}
            {badge > 0 && (
              <span data-testid={`review-nav-badge-${n.id}`} className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                style={{ backgroundColor: active ? "#fff" : "#F97316", color: active ? "#F97316" : "#fff" }}>{badge}</span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

const DECISION_INPUT_STYLE = { backgroundColor: "#0A1F44", border: "1px solid #1E3A8A", color: "#fff" };

function DecisionToggle({ decision, completed, open, unread, onToggle }) {
  return (
    <button
      data-testid={`decision-toggle-${decision.id}`}
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        {completed
          ? <CheckCircle2 size={17} style={{ color: "#10B981" }} className="flex-shrink-0" />
          : <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: "#F97316" }} />}
        <span className="text-white text-sm font-medium truncate">{decision.title}</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {unread > 0 && (
          <span data-testid={`decision-unread-${decision.id}`} title={`${unread} new repl(ies) from 360 Safety Systems`}
            className="flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full"
            style={{ backgroundColor: "#F97316", color: "#fff" }}>
            <MessageCircle size={11} /> {unread}
          </span>
        )}
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
          style={completed ? { backgroundColor: "rgba(16,185,129,0.15)", color: "#10B981" } : { backgroundColor: "rgba(249,115,22,0.15)", color: "#F97316" }}>
          {completed ? "Complete" : "Open"}
        </span>
        <ChevronDown size={17} className={`text-[#9CA3AF] transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
    </button>
  );
}

function DecisionThread({ decision }) {
  const responses = decision.responses || [];
  if (!responses.length) return null;
  return (
    <div className="space-y-2">
      <p className="text-xs uppercase tracking-wider text-[#9CA3AF]">Conversation</p>
      {responses.map((r) => (
        <ReplyBubble key={r.id} author={r.name} isAdmin={r.author_role === "admin"} text={r.response_text}
          date={r.created_at} dark testId={`review-decision-msg-${r.id}`} />
      ))}
    </div>
  );
}

function OptionPills({ decision, selected, custom, onPick }) {
  const options = decision.options || [];
  if (options.length === 0) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-2">Select an option</p>
      <div className="flex flex-wrap gap-2">
        {options.map((o, i) => {
          const active = selected === o && !custom.trim();
          return (
            <button
              key={`${o}-${i}`}
              data-testid={`decision-option-${decision.id}-${i}`}
              onClick={() => onPick(o)}
              className="text-sm px-3.5 py-2 rounded-full border font-medium transition-all"
              style={active
                ? { backgroundColor: "#F97316", borderColor: "#F97316", color: "#fff" }
                : { borderColor: "#1E3A8A", color: "#fff", backgroundColor: "#112A57" }}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DecisionForm({ decision, name, setName, selected, setSelected, custom, setCustom, busy, onSubmit }) {
  const options = decision.options || [];
  return (
    <div className="space-y-3 mt-1">
      <OptionPills decision={decision} selected={selected} custom={custom}
        onPick={(o) => { setSelected(o); setCustom(""); }} />

      <div>
        <p className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-1.5">
          {options.length ? "Or enter a custom response" : "Your response"}
        </p>
        <textarea
          data-testid={`decision-response-${decision.id}`}
          value={custom}
          onChange={(e) => { setCustom(e.target.value); if (e.target.value) setSelected(null); }}
          rows={2}
          placeholder="Type a custom response..."
          className="w-full rounded-lg px-3 py-2.5 outline-none resize-y text-sm" style={DECISION_INPUT_STYLE}
        />
      </div>

      <input
        data-testid={`decision-name-${decision.id}`}
        value={name} onChange={(e) => setName(e.target.value)}
        placeholder="Your name"
        className="w-full rounded-lg px-3 py-2.5 outline-none text-sm" style={DECISION_INPUT_STYLE}
      />

      <button
        data-testid={`decision-submit-${decision.id}`}
        onClick={onSubmit} disabled={busy}
        className="flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-white text-sm transition-all disabled:opacity-60"
        style={{ backgroundColor: "#F97316" }}
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Submit Decision
      </button>
    </div>
  );
}

function DecisionSections({ decision }) {
  const [openKeys, setOpenKeys] = useState({});
  const filled = DECISION_SECTIONS.filter((s) => (decision[s.key] || "").trim());
  if (filled.length === 0) return null;
  return (
    <div className="space-y-2 mt-3">
      {filled.map((s) => {
        const isOpen = !!openKeys[s.key];
        return (
          <div key={s.key} className="rounded-lg border overflow-hidden" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
            <button
              data-testid={`decision-section-toggle-${decision.id}-${s.key.replace(/_/g, "-")}`}
              onClick={() => setOpenKeys((prev) => ({ ...prev, [s.key]: !prev[s.key] }))}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left"
            >
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#F97316" }}>{s.label}</span>
              <ChevronDown size={15} className={`text-[#9CA3AF] flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <p data-testid={`decision-section-content-${decision.id}-${s.key.replace(/_/g, "-")}`}
                className="px-3 pb-3 text-[#cbd5e1] text-sm leading-relaxed whitespace-pre-line">
                {decision[s.key]}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DecisionBody({ completed, readOnly, formProps, onReply }) {
  const decision = formProps.decision;
  return (
    <>
      <DecisionThread decision={decision} />
      {!completed && readOnly && (
        <p className="text-sm text-[#9CA3AF] italic" data-testid={`decision-awaiting-${decision.id}`}>Awaiting client response.</p>
      )}
      {!completed && !readOnly && <DecisionForm {...formProps} />}
      {completed && !readOnly && (
        <ReplyToggle dark onSend={onReply} testPrefix={`review-decision-reply-${decision.id}`}
          placeholder="Reply to 360 Safety Systems..." />
      )}
    </>
  );
}

function useDecisionActions(docId, decision, onSubmitted, readOnly) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState(null);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const responses = decision.responses || [];
  const completed = responses.some((r) => r.author_role !== "admin");
  const unreadReplies = responses.filter((r) => r.author_role === "admin" && r.client_unread).length;

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && unreadReplies > 0 && !readOnly) {
      api.post(`/documents/${docId}/decisions/${decision.id}/client-read`).then(onSubmitted).catch(() => {});
    }
  };

  const submit = async () => {
    const responseText = custom.trim() || selected;
    if (!name.trim() || !responseText) {
      toast.error("Please enter your name and select or type a response");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/documents/${docId}/decisions/${decision.id}/responses`, {
        name: name.trim(), response_text: responseText,
      });
      toast.success("Decision submitted");
      setSelected(null); setCustom(""); setName("");
      onSubmitted();
    } catch {
      toast.error("Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  const sendFollowUp = async (text) => {
    await api.post(`/documents/${docId}/decisions/${decision.id}/responses`, { name: "", response_text: text });
    toast.success("Reply sent to 360 Safety Systems");
    onSubmitted();
  };

  return {
    open, toggleOpen, completed, unreadReplies, sendFollowUp,
    formProps: { decision, name, setName, selected, setSelected, custom, setCustom, busy, onSubmit: submit },
  };
}

function DecisionBlock({ docId, decision, onSubmitted, readOnly }) {
  const a = useDecisionActions(docId, decision, onSubmitted, readOnly);
  return (
    <div className="rounded-lg border overflow-hidden" style={{ backgroundColor: "#0A1F44", borderColor: "#1E3A8A" }}>
      <DecisionToggle decision={decision} completed={a.completed} open={a.open} unread={a.unreadReplies} onToggle={a.toggleOpen} />
      {a.open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t" style={{ borderColor: "#1E3A8A" }}>
          <DecisionSections decision={decision} />
          <DecisionBody completed={a.completed} readOnly={readOnly} onReply={a.sendFollowUp} formProps={a.formProps} />
        </div>
      )}
    </div>
  );
}

const fmtShortDate = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

function ConsultationStamp({ d }) {
  if (!d.consultation_proceeded_at) return null;
  return (
    <span data-testid={`consultation-stamp-${d.id}`} className="text-[11px] mt-1" style={{ color: "#94A3B8" }}>
      Consultation: {fmtShortDate(d.consultation_proceeded_at)}
    </span>
  );
}

function ApprovalAction({ d, onApprove, onProceed, readOnly }) {
  if (d.status === "Approved") {
    return (
      <div className="flex flex-col items-end">
        <span data-testid={`review-approved-${d.id}`} className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "#10B981" }}>
          <CheckCircle2 size={16} /> Approved · locked
        </span>
        <ConsultationStamp d={d} />
      </div>
    );
  }
  if (!readOnly && d.status === "Pending Approval" && d.main_file) {
    return (
      <div className="flex flex-col items-end">
        <button data-testid={`review-approve-${d.id}`} onClick={() => onApprove(d)}
          className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white transition-colors"
          style={{ backgroundColor: "#10B981" }}>
          <CheckCircle2 size={16} /> Approve Document
        </button>
        <ConsultationStamp d={d} />
      </div>
    );
  }
  if (!readOnly && d.status === "In Review" && !d.consultation_proceeded_at) {
    return (
      <button data-testid={`review-proceed-${d.id}`} onClick={() => onProceed(d)}
        className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white transition-colors"
        style={{ backgroundColor: "#14B8A6" }}>
        <ArrowRightCircle size={16} /> Proceed to Consultation
      </button>
    );
  }
  if (d.consultation_proceeded_at) {
    return (
      <span data-testid={`consultation-proceeded-${d.id}`} className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "#14B8A6" }}>
        <CheckCircle2 size={16} /> Proceeded to consultation — {fmtShortDate(d.consultation_proceeded_at)}
      </span>
    );
  }
  return null;
}

function ApprovalBar({ d, onApprove, onProceed, readOnly }) {
  return (
    <div className="mt-4 flex items-center justify-between gap-3 flex-wrap rounded-lg px-3 py-2.5"
      style={{ backgroundColor: "#0A1F44", border: "1px solid #1E3A8A" }}>
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-[#9CA3AF]">Status</span>
        <span data-testid={`review-status-${d.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
          style={{ backgroundColor: `${STATUS_COLORS[d.status]}22`, color: STATUS_COLORS[d.status] }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[d.status] }} />
          {d.status}
        </span>
      </div>
      <ApprovalAction d={d} onApprove={onApprove} onProceed={onProceed} readOnly={readOnly} />
    </div>
  );
}

function ReviewDocHeader({ d, view, onWorkspace }) {
  const { isNewForMe } = useNewFlags();
  const openMain = () => {
    if (onWorkspace && WORKING_STATUSES.includes(d.status)) { onWorkspace(d); return; }
    view({ file: d.main_file, title: d.title });
  };
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-2 min-w-0">
        <FileText size={18} style={{ color: "#F97316" }} />
        <h2 className="font-heading text-white text-lg font-semibold truncate">{d.title}</h2>
        {isNewForMe(d) && <NewBadge testId={`review-new-${d.id}`} />}
        {d.blocker && <BlockerBadge iconOnly note={d.blocker_note} testId={`review-blocker-${d.id}`} />}
        {d.client_unread_count > 0 && (
          <span data-testid={`review-doc-unread-${d.id}`} title={`${d.client_unread_count} new repl(ies) from 360 Safety Systems`}
            className="flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: "#F97316", color: "#fff" }}>
            <MessageCircle size={11} /> {d.client_unread_count}
          </span>
        )}
      </div>
      <div className="flex gap-2 flex-shrink-0">
        {d.main_file && (
          <button data-testid={`review-view-main-${d.id}`} onClick={openMain}
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg text-white" style={{ backgroundColor: "#F97316" }}>
            <Eye size={15} /> View Document
          </button>
        )}
        {d.summary_sheet && (
          <button data-testid={`review-view-summary-${d.id}`} onClick={() => view({ file: d.summary_sheet, title: `${d.title} — Summary` })}
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg text-white border" style={{ borderColor: "#1E3A8A" }}>
            <Eye size={15} /> View Summary
          </button>
        )}
      </div>
    </div>
  );
}

function BlockerNoteSection({ d }) {
  if (!d.blocker || !d.blocker_note) return null;
  return (
    <div data-testid={`review-blocker-note-${d.id}`} className="mt-4 rounded-lg px-3.5 py-3 border-l-4"
      style={{ backgroundColor: "rgba(255,230,6,0.08)", borderColor: "#ffe606" }}>
      <p className="text-[10px] font-extrabold uppercase tracking-widest flex items-center gap-1.5" style={{ color: "#ffe606" }}>
        <AlertTriangle size={12} /> Blocker
      </p>
      <p className="text-sm text-white mt-1 leading-relaxed whitespace-pre-line">{d.blocker_note}</p>
    </div>
  );
}

function ReviewDecisionsSection({ d, onReload, readOnly }) {
  if (!d.decisions?.length) return null;
  return (
    <div className="mt-5 pt-4 border-t" style={{ borderColor: "#1E3A8A" }}>
      <p className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-3">Client Decisions</p>
      <div className="space-y-2.5">
        {d.decisions.map((dec) => (
          <DecisionBlock key={dec.id} docId={d.id} decision={dec} onSubmitted={onReload} readOnly={readOnly} />
        ))}
      </div>
    </div>
  );
}

function ReviewDocCard({ d, onView, onApprove, onProceed, onReload, readOnly, onWorkspace }) {
  const { markSeen } = useNewFlags();
  const isUpload = d.category === "Client uploads";
  const view = (v) => { markSeen(d); onView({ ...v, allowDownload: d.downloadable }); };
  const openWorkspace = (docItem) => { markSeen(docItem); onWorkspace(docItem); };
  return (
    <div data-testid={`review-doc-${d.id}`} className="rounded-xl border p-5"
      style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
      <ReviewDocHeader d={d} view={view} onWorkspace={onWorkspace ? openWorkspace : null} />

      {isUpload ? (
        <p className="text-xs text-[#9CA3AF] mt-3" data-testid={`upload-info-${d.id}`}>
          Uploaded {fmtShortDate(d.created_at)} via {d.uploaded_via || "Review Portal"}
        </p>
      ) : (
        <ApprovalBar d={d} onApprove={onApprove} onProceed={onProceed} readOnly={readOnly} />
      )}

      {(d.attachments || []).some((a) => !a.portals || a.portals.includes("review")) && (
        <div className="mt-4"><AttachmentList attachments={(d.attachments || []).filter((a) => !a.portals || a.portals.includes("review"))} onView={view} /></div>
      )}

      <BlockerNoteSection d={d} />

      <ReviewDecisionsSection d={d} onReload={onReload} readOnly={readOnly} />
    </div>
  );
}

function ReviewLibrary({ docs, onView, onApprove, onProceed, onReload, readOnly, onWorkspace }) {
  if (docs.length === 0) {
    return (
      <div className="text-center py-20 text-[#9CA3AF]" data-testid="review-empty">
        <ClipboardList size={40} className="mx-auto mb-3 opacity-40" />
        No documents available yet.
      </div>
    );
  }
  return (
    <div className="space-y-8">
      {CATEGORY_GROUPS.map((g) => {
        const groupDocs = docs.filter((d) => d.category === g.cat);
        if (groupDocs.length === 0) return null;
        return (
          <div key={g.cat} data-testid={`review-group-${g.cat}`}>
            <div className="flex items-center gap-2 mb-3 px-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#F97316" }} />
              <h3 className="font-heading text-white text-sm font-bold uppercase tracking-wider">{g.label}</h3>
              <span className="text-xs text-[#9CA3AF]">({groupDocs.length})</span>
            </div>
            <div className="space-y-4">
              {groupDocs.map((d) => <ReviewDocCard key={d.id} d={d} onView={onView} onApprove={onApprove} onProceed={onProceed} onReload={onReload} readOnly={readOnly} onWorkspace={onWorkspace} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ApproveModal({ target, approving, onCancel, onConfirm }) {
  return createPortal(
    <div data-testid="approve-modal" className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(10,31,68,0.7)" }}>
      <div className="rounded-2xl w-full max-w-md p-6 border" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(16,185,129,0.15)" }}>
          <ShieldCheck size={22} style={{ color: "#10B981" }} />
        </div>
        <h3 className="font-heading text-white text-xl font-bold mb-2">Approve “{target.title}”?</h3>
        <p className="text-[#cbd5e1] text-sm leading-relaxed">
          Do you wish to approve this document? Once approved, this document will be locked from further approval workflow changes.
        </p>
        <div className="flex gap-3 mt-6">
          <button data-testid="approve-cancel" onClick={onCancel}
            className="flex-1 rounded-lg px-4 py-2.5 font-medium text-white border" style={{ borderColor: "#1E3A8A" }}>
            Cancel
          </button>
          <button data-testid="approve-confirm" onClick={onConfirm} disabled={approving}
            className="flex-1 rounded-lg px-4 py-2.5 font-semibold text-white disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ backgroundColor: "#10B981" }}>
            {approving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            Confirm Approval
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ProceedModal({ target, busy, onCancel, onConfirm }) {
  return createPortal(
    <div data-testid="proceed-modal" className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(10,31,68,0.7)" }}>
      <div className="rounded-2xl w-full max-w-md p-6 border" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(20,184,166,0.15)" }}>
          <ArrowRightCircle size={22} style={{ color: "#14B8A6" }} />
        </div>
        <h3 className="font-heading text-white text-xl font-bold mb-2">Proceed to consultation for “{target.title}”?</h3>
        <p className="text-[#cbd5e1] text-sm leading-relaxed">
          This records that the document is ready to move to consultation. The document's status will not change yet —
          360 Safety Systems will move it to In Consultation once the accompanying consultation documents are ready.
        </p>
        <div className="flex gap-3 mt-6">
          <button data-testid="proceed-cancel" onClick={onCancel}
            className="flex-1 rounded-lg px-4 py-2.5 font-medium text-white border" style={{ borderColor: "#1E3A8A" }}>
            Cancel
          </button>
          <button data-testid="proceed-confirm" onClick={onConfirm} disabled={busy}
            className="flex-1 rounded-lg px-4 py-2.5 font-semibold text-white disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ backgroundColor: "#14B8A6" }}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRightCircle size={16} />}
            Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function UploadFields({ title, setTitle, file, setFile }) {
  return (
    <div className="space-y-3">
      <input data-testid="upload-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
        placeholder="Document name (e.g. Toolbox meeting minutes — 19 Jul 2026)"
        className="w-full rounded-lg px-3 py-2.5 text-white outline-none text-sm"
        style={{ backgroundColor: "#0A1F44", border: "1px solid #1E3A8A" }} />
      <label className="flex items-center gap-2 text-sm text-[#9CA3AF] cursor-pointer hover:text-white transition-colors rounded-lg px-3 py-2.5"
        style={{ backgroundColor: "#0A1F44", border: "1px solid #1E3A8A" }} data-testid="upload-file-label">
        <FileText size={16} style={{ color: "#F97316" }} />
        {file ? file.name : "Choose a file (PDF, Word, Excel, image)"}
        <input type="file" data-testid="upload-file-input" className="hidden"
          onChange={(e) => setFile(e.target.files[0] || null)} />
      </label>
    </div>
  );
}

function useClientUpload(clientId, onUploaded, onClose) {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Please enter a document name"); return; }
    if (!file) { toast.error("Please choose a file"); return; }
    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/client-uploads`, fd);
      toast.success("Document uploaded");
      onUploaded();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return { title, setTitle, file, setFile, busy, submit };
}

function UploadDocumentModal({ clientId, onClose, onUploaded }) {
  const { title, setTitle, file, setFile, busy, submit } = useClientUpload(clientId, onUploaded, onClose);

  return createPortal(
    <div data-testid="upload-document-modal" className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(10,31,68,0.7)" }}>
      <div className="rounded-2xl w-full max-w-md p-6 border" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(249,115,22,0.15)" }}>
          <Upload size={22} style={{ color: "#F97316" }} />
        </div>
        <h3 className="font-heading text-white text-xl font-bold mb-1">Upload Document</h3>
        <p className="text-[#9CA3AF] text-sm mb-4">
          For records generated outside the document-development workflow — toolbox meeting minutes, consultation records,
          meeting records and other operational documents. Filed under Client Uploads.
        </p>
        <UploadFields title={title} setTitle={setTitle} file={file} setFile={setFile} />
        <div className="flex gap-3 mt-5">
          <button data-testid="upload-cancel" onClick={onClose}
            className="flex-1 rounded-lg px-4 py-2.5 font-medium text-white border" style={{ borderColor: "#1E3A8A" }}>
            Cancel
          </button>
          <button data-testid="upload-submit" onClick={submit} disabled={busy}
            className="flex-1 rounded-lg px-4 py-2.5 font-semibold text-white disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ backgroundColor: "#F97316" }}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            Upload
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function useConfirmAction({ run, success, failure }, onDone) {
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await run(target);
      toast.success(success);
      setTarget(null);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || failure);
    } finally {
      setBusy(false);
    }
  };

  return { target, setTarget, busy, confirm };
}

function DocumentsTab({ clientId, docs, readOnly, onView, onApprove, onProceed, onUpload, onReload, onWorkspace }) {
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const filtered = filterDocs(docs, search, filterCat, filterStatus);
  return (
    <>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-white text-3xl font-bold">Document Library & Decisions</h1>
          <p className="text-[#9CA3AF] mt-1 mb-8">View documents and respond to client decisions for your organisation.</p>
        </div>
        {!readOnly && (
          <button data-testid="review-upload-document" onClick={onUpload}
            className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex-shrink-0"
            style={{ backgroundColor: "rgba(249,115,22,0.15)", color: "#F97316" }}>
            <Upload size={16} /> Upload Document
          </button>
        )}
      </div>
      <ReviewWindowCards clientId={clientId} />
      <DocSearchBar search={search} setSearch={setSearch} category={filterCat} setCategory={setFilterCat}
        status={filterStatus} setStatus={setFilterStatus} dark testPrefix="review-doc" />
      {docs.length > 0 && filtered.length === 0 ? (
        <div className="rounded-xl border p-12 text-center text-[#9CA3AF]" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }} data-testid="review-doc-no-match">No documents match your search.</div>
      ) : (
        <ReviewLibrary docs={filtered} onView={onView} onApprove={onApprove} onProceed={onProceed} onReload={onReload} readOnly={readOnly} onWorkspace={onWorkspace} />
      )}
    </>
  );
}

function ReviewModals({ clientId, approve, proceed, showUpload, onCloseUpload, onUploaded }) {
  return (
    <>
      {approve.target && (
        <ApproveModal target={approve.target} approving={approve.busy}
          onCancel={() => approve.setTarget(null)} onConfirm={approve.confirm} />
      )}
      {proceed.target && (
        <ProceedModal target={proceed.target} busy={proceed.busy}
          onCancel={() => proceed.setTarget(null)} onConfirm={proceed.confirm} />
      )}
      {showUpload && (
        <UploadDocumentModal clientId={clientId} onClose={onCloseUpload} onUploaded={onUploaded} />
      )}
    </>
  );
}

function useReviewActions(load) {
  const approve = useConfirmAction({
    run: (t) => api.post(`/documents/${t.id}/approve`),
    success: "Document approved",
    failure: "Failed to approve document",
  }, load);

  const proceed = useConfirmAction({
    run: (t) => api.post(`/documents/${t.id}/proceed-consultation`),
    success: "Recorded — the document will move to consultation shortly",
    failure: "Could not record the action",
  }, load);

  return { approve, proceed };
}

function ReviewTabContent({ view, clientId, docs, readOnly, onView, approve, proceed, onUpload, load, onWorkspace }) {
  if (view === "feedback") return <ReviewFeedbackTab clientId={clientId} readOnly={readOnly} onChanged={load} />;
  if (view === "billing") return <ClientAccount clientId={clientId} readOnly={readOnly} />;
  return (
    <DocumentsTab clientId={clientId} docs={docs} readOnly={readOnly}
      onView={onView} onApprove={approve.setTarget} onProceed={proceed.setTarget}
      onUpload={onUpload} onReload={load} onWorkspace={onWorkspace} />
  );
}

function useReviewData(clientId, onUnreadChange) {
  const [docs, setDocs] = useState([]);
  const [features, setFeatures] = useState({ implementation: false, account: false });
  const [unread, setUnread] = useState({ feedback: 0, decisions: 0, total: 0, doc_blockers: 0, billing_blockers: 0 });

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/documents`).then(({ data }) => setDocs(data));
    api.get(`/clients/${clientId}/features`).then(({ data }) => setFeatures(data));
    api.get(`/clients/${clientId}/review-unread`).then(({ data }) => {
      setUnread(data);
      if (onUnreadChange) onUnreadChange(data);
    });
  }, [clientId, onUnreadChange]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // keep status live across portals
    return () => clearInterval(t);
  }, [load]);

  return { docs, features, unread, load };
}

export function ReviewView({ clientId, readOnly = false, view: viewProp, onViewChange, onUnreadChange }) {
  const [viewing, setViewing] = useState(null);
  const [internalView, setInternalView] = useState("documents");
  const [showUpload, setShowUpload] = useState(false);
  const [workspaceDoc, setWorkspaceDoc] = useState(null);
  const view = viewProp !== undefined ? viewProp : internalView;
  const setView = onViewChange || setInternalView;

  const { docs, features, unread, load } = useReviewData(clientId, onUnreadChange);
  const { approve, proceed } = useReviewActions(load);

  const navItems = REVIEW_NAV.filter((n) => n.id !== "billing" || features.account);
  const effectiveView = navItems.some((n) => n.id === view) ? view : "documents";
  const liveWorkspaceDoc = workspaceDoc ? (docs.find((x) => x.id === workspaceDoc.id) || workspaceDoc) : null;

  return (
    <>
      <div className="max-w-4xl mx-auto">
        <ReviewNav items={navItems} view={effectiveView} onChange={setView}
          badges={{ documents: unread.decisions, feedback: unread.feedback }}
          warnBadges={{ documents: unread.doc_blockers, billing: unread.billing_blockers }} />
        <ReviewTabContent view={effectiveView} clientId={clientId} docs={docs} readOnly={readOnly}
          onView={setViewing} approve={approve} proceed={proceed}
          onUpload={() => setShowUpload(true)} load={load} onWorkspace={setWorkspaceDoc} />
      </div>
      {liveWorkspaceDoc && (
        <ReviewWorkspace doc={liveWorkspaceDoc} clientId={clientId} role="review" previewOnly={readOnly}
          onClose={() => setWorkspaceDoc(null)} />
      )}
      {viewing && <DocViewer file={viewing.file} title={viewing.title} allowDownload={!!viewing.allowDownload} onClose={() => setViewing(null)} />}
      {!readOnly && (
        <ReviewModals clientId={clientId} approve={approve} proceed={proceed}
          showUpload={showUpload} onCloseUpload={() => setShowUpload(false)} onUploaded={load} />
      )}
    </>
  );
}

function ReviewBell({ unread, onClick }) {
  return (
    <button data-testid="review-bell" onClick={onClick} title="Replies from 360 Safety Systems"
      className="relative p-2 rounded-lg hover:bg-white/5 text-[#9CA3AF] hover:text-white transition-colors">
      <Bell size={18} />
      {unread.total > 0 && (
        <span data-testid="review-bell-count"
          className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
          style={{ backgroundColor: "#F97316", color: "#fff" }}>{unread.total}</span>
      )}
    </button>
  );
}

export default function ReviewPortal() {
  const { auth } = useAuth();
  const [view, setView] = useState("documents");
  const [unread, setUnread] = useState({ feedback: 0, decisions: 0, total: 0 });
  const bellTarget = unread.feedback > 0 || unread.decisions === 0 ? "feedback" : "documents";
  return (
    <DarkPortalShell subtitle="Review Portal"
      headerExtra={<ReviewBell unread={unread} onClick={() => setView(bellTarget)} />}>
      <ReviewView clientId={auth.client_id} view={view} onViewChange={setView} onUnreadChange={setUnread} />
    </DarkPortalShell>
  );
}
